import { createFileRoute } from "@tanstack/react-router"
import { GuidePage } from "@/components/pages/GuidePage"
import { fetchExploreGuides, type PublicGuideRow } from "@/lib/explore-data"
import { defaultMeta, absoluteUrl, SITE_NAME } from "@/lib/seo"
import { guideSlug } from "@/lib/slugify"

export const Route = createFileRoute(`/_with-optional-sidebar/trips/explore`)({
  // Full SSR so the intro/explainer prose AND every guide's <a href> land in the
  // raw HTML for crawlers. The leaf flag is honored because both ancestor gates
  // (rootSsr + the layout's optionalSidebarSsr) also return true for this URL.
  ssr: true,
  // Server-render the same published-guide set the client query would fetch, so
  // the grid isn't an empty client-only shell. Direct Postgres (never a
  // self-fetch to /api/trpc — deadlocks). Never throws: degrades to [].
  loader: () => fetchExploreGuides(),
  // Default param so the unit test can call head() with no context — TanStack
  // always passes the real { loaderData, ... } at runtime.
  head: ({ loaderData = [] }: { loaderData?: PublicGuideRow[] } = {}) => {
    const base = defaultMeta({
      title: `Explore Travel Guides`,
      description: `Browse published travel guides from the ChicTravel community and copy full itineraries into your own trip plan.`,
      path: `/trips/explore`,
    })
    // CollectionPage + ItemList parity with the /destinations index so the
    // explore hub emits structured data in the raw HTML (full SSR route). Links
    // are canonical on short_id, matching every other guide surface.
    const jsonLd = {
      "@context": `https://schema.org`,
      "@type": `CollectionPage`,
      name: `Explore Travel Guides`,
      url: absoluteUrl(`/trips/explore`),
      publisher: {
        "@type": `Organization`,
        name: SITE_NAME,
        url: absoluteUrl(`/`),
      },
      mainEntity: {
        "@type": `ItemList`,
        itemListElement: loaderData.map((g, i) => ({
          "@type": `ListItem`,
          position: i + 1,
          name: g.name || g.destination,
          url: absoluteUrl(`/guide/${guideSlug(g)}/${g.short_id}`),
        })),
      },
    }
    return {
      ...base,
      scripts: [
        { type: `application/ld+json`, children: JSON.stringify(jsonLd) },
      ],
    }
  },
  component: ExploreRoute,
})

function ExploreRoute() {
  const initialGuides = Route.useLoaderData()
  return <GuidePage initialGuides={initialGuides} />
}
